import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Form, Input } from 'antd';
import Modal from 'components/Modal';
import styled from 'styled-components';
import TextArea from 'antd/es/input/TextArea';
import InputEmail from 'components/InputEmail';
import InputPhone from 'components/InputPhone';
import { toast } from 'react-toastify';
import settingApis from 'features/settingService/services/apis';
import { ICreateSupplierBody } from 'features/settingService/services/types/api';
import { IResSupplier } from 'features/settingService/services/types/supplier';

const initialValues: FormType = {
  id: '',
  name: '',
  phone: '',
  email: '',
  website: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  description: '',
};

type FormType = {
  id: string;
  name: string;
  phone: string;
  email: string;
  website: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  description: string;
};

type Props = {};

export interface IModalAddOrUpdateSupplierRef {
  open: (data?: IResSupplier, onSuccess?: () => void) => void;
}

const AddOrUpdateSupplierModal = forwardRef<IModalAddOrUpdateSupplierRef, Props>((_, ref) => {
  const [form] = Form.useForm<FormType>();
  const [visible, setVisible] = useState(false);
  const [data, setData] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const refSuccess = useRef<() => void>();

  const handleClose = () => {
    setVisible(false);
    setData(null);
    form.resetFields();
  };

  const handleSubmit = () => {
    form.submit();
  };

  const onFinish = async (values: FormType) => {
    try {
      setLoading(true);

      const body: ICreateSupplierBody = {
        ...(data?.id ? { id: data?.id } : {}),
        name: values.name,
        phone: values.phone,
        email: values.email,
        website: values.website,
        address: values.address,
        city: values.city,
        state: values.state,
        zip: values.zip,
        description: values.description,
      };

      const res = await settingApis.createOrUpdateSupplier(body);
      if (res?.data?.data) {
        toast.success('Supplier created successfully');
        refSuccess.current?.();
        handleClose();
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    open: (data?: IResSupplier, onSuccess?: () => void) => {
      setVisible(true);
      refSuccess.current = onSuccess;
      if (data) {
        setData(data);
        form.setFieldsValue(data);
      }
    },
  }));

  return (
    <Modal
      v2
      noneBodyStyle
      width={1060}
      visible={visible}
      onClose={handleClose}
      onSubmit={handleSubmit}
      modalTitle={data ? 'Edit Supplier' : 'New Supplier'}
      okTitle={data ? 'Update' : 'Add'}
      disableOk={loading}
    >
      <Form form={form} onFinish={onFinish} initialValues={initialValues} layout='vertical'>
        <Wrapper>
          <Form.Item
            name='name'
            label={
              <span>
                Supplier Name <span className='required'>*</span>
              </span>
            }
            rules={[{ required: true, message: 'Supplier name is required' }]}
          >
            <Input placeholder='Enter your supplier name' size='large' />
          </Form.Item>

          <Form.Item
            name='phone'
            label={
              <span>
                Phone <span className='required'>*</span>
              </span>
            }
            rules={[{ required: true, message: 'Phone is required' }]}
          >
            <InputPhone placeholder='Enter your phone number' size='large' />
          </Form.Item>

          <InputEmail
            name='email'
            label={
              <span>
                Email <span className='required'>*</span>
              </span>
            }
            rules={[
              { required: true, message: 'Email is required' },
              { type: 'email', message: 'Please enter a valid email address' },
            ]}
          />

          <Form.Item name='website' label='Website' rules={[{ required: true, message: 'Website is required' }]}>
            <Input placeholder='Enter your website' size='large' />
          </Form.Item>

          <Form.Item name='address' label='Street' rules={[{ required: true, message: 'Address is required' }]}>
            <Input placeholder='Enter your address' size='large' />
          </Form.Item>

          <ZoneContainer>
            <div className='item'>
              <Form.Item name='city' label='City'>
                <Input placeholder='Enter your city' size='large' />
              </Form.Item>
            </div>
            <div className='item'>
              <Form.Item name='state' label='State'>
                <Input placeholder='Enter your state' size='large' />
              </Form.Item>
            </div>
            <div className='item'>
              <Form.Item name='zip' label='Zip Code'>
                <Input placeholder='Enter your zip code' size='large' />
              </Form.Item>
            </div>
          </ZoneContainer>

          <Form.Item name='description' label='Note'>
            <TextArea rows={3} placeholder='Enter your note' size='large' />
          </Form.Item>
        </Wrapper>
      </Form>
    </Modal>
  );
});

AddOrUpdateSupplierModal.displayName = 'AddOrUpdateSupplierModal';

export default AddOrUpdateSupplierModal;

const Wrapper = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;

  .ant-form-item {
    margin-bottom: 16px;
    .ant-form-item-label {
      font-size: 18px;
      .required {
        color: #d2464f;
      }
    }
  }
`;

const ZoneContainer = styled.div`
  display: flex;
  gap: 16px;
  .item {
    flex: 1;
  }
`;
